import { useMemo, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import type {
  BusinessHours,
  CampaignC1Variant,
  CampaignCadence,
  CampaignConfig,
  CampaignTemplates,
} from '@sdr-agend/admin-schemas'

/**
 * Sub-form: cadence + templates dropdown (filtered to APPROVED) + prompt_id
 * dropdown + business_hours_override toggle. Pure controlled component — the
 * parent owns the `CampaignConfig` state and handles submission.
 *
 * Story 3.8 §AC #6: this form is rendered on the detail page
 * (`/admin/campaigns/:id`) and on the create form (`/admin/campaigns/new`).
 *
 * The list of templates passed in `approvedTemplates` should already be
 * filtered to `status='APPROVED'`, `active=true` by the caller (parent fetches
 * via `/admin/templates?status=APPROVED`). Idioma NÃO é mais filtrado — o
 * operador escolhe qualquer template aprovado; o label exibe o idioma.
 */

export interface ApprovedTemplateOption {
  /** Template name — what's persisted in `campaign.config.templates.cN`. */
  name: string
  /** Human-readable label (typically `name (language)`). */
  label: string
}

export interface PromptOption {
  /** prompt_id — UUID. */
  id: string
  /** Human-readable label, e.g. "v3 (active)". */
  label: string
}

interface CampaignConfigFormProps {
  value: CampaignConfig
  onChange: (next: CampaignConfig) => void
  /** Approved + active pt_BR templates available to the operator. */
  approvedTemplates: ReadonlyArray<ApprovedTemplateOption>
  /** Prompts bound to this campaign (Story 3.7); empty list = no prompts yet. */
  prompts: ReadonlyArray<PromptOption>
  disabled?: boolean
  /**
   * Story 3.20 follow-up (user feedback 2026-07-15) — `lead_new` never reads
   * the legacy C1-C4 cadence/templates below (confirmed: lead-new.worker.ts
   * never touches config.templates.c1/c2/c3), so showing that section next
   * to FollowupConfigSection was confusing, not just harmless dead weight.
   * Callers for campaign types that still run on the legacy cadence engine
   * should leave this `false`/omitted; `CampaignDetail.tsx` passes `true` for
   * `type === 'lead_new'`. Once 3.21/3.22 migrate confirmation/reactivation
   * onto `followup` too, `value.followup !== undefined` (checked internally)
   * covers those cases without this prop needing to change.
   */
  hideLegacyCadence?: boolean
}

const WEEKDAY_LABELS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
]

const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  timezone: 'America/Sao_Paulo',
  start: '09:00',
  end: '18:00',
  weekdays: [1, 2, 3, 4, 5],
}

// 2026-07-30 (user request) — A/B/C test do C1 (confirmação inicial). Letters
// give a friendly default `key` suggestion; the cap matches
// `CampaignC1Variant`'s `maxItems: 10` in the admin-schemas contract.
// Mirrors `FollowupConfigSection.tsx`'s identical constants/helpers for
// lead_new's opening-template variants — kept as a separate small copy here
// rather than a shared import since the two sections have no other coupling.
const VARIANT_KEY_LETTERS = 'ABCDEFGHIJ'.split('')
const MAX_C1_VARIANTS = VARIANT_KEY_LETTERS.length

function newVariantId(): string {
  const cryptoObj = globalThis.crypto
  return typeof cryptoObj?.randomUUID === 'function'
    ? cryptoObj.randomUUID()
    : `c1-variant-${Date.now()}`
}

function nextC1VariantKey(existing: ReadonlyArray<CampaignC1Variant>): string {
  const used = new Set(existing.map((v) => v.key))
  const letter = VARIANT_KEY_LETTERS.find((l) => !used.has(l))
  return letter ?? `V${existing.length + 1}`
}

export function CampaignConfigForm({
  value,
  onChange,
  approvedTemplates,
  prompts,
  disabled = false,
  hideLegacyCadence = false,
}: CampaignConfigFormProps): React.ReactElement {
  const overrideEnabled = value.business_hours_override !== null

  const templateNames = useMemo(() => approvedTemplates.map((t) => t.name), [approvedTemplates])

  // C4 ("lembrete na hora") template referenced but no longer in the approved
  // list → render a disabled placeholder option so the operator sees the drift.
  const c4OptionMissing =
    (value.templates.c4 ?? '') !== '' && !templateNames.includes(value.templates.c4 ?? '')

  function setCadence(patch: Partial<CampaignCadence>): void {
    onChange({ ...value, cadence: { ...value.cadence, ...patch } })
  }

  function setTemplate(slot: keyof CampaignTemplates, name: string): void {
    onChange({ ...value, templates: { ...value.templates, [slot]: name } })
  }

  const c1Variants = value.templates.c1_variants ?? []

  // 2026-07-30 — same stable-id-per-row rationale as `FollowupConfigSection`'s
  // `variantIdsRef`: `CampaignC1Variant` has no persisted id, and `key`
  // is user-editable text, so neither is safe as a React list key (typing
  // into the key field would remount the row mid-keystroke).
  const c1VariantIdsRef = useRef<string[]>(c1Variants.map(() => newVariantId()))
  if (c1VariantIdsRef.current.length !== c1Variants.length) {
    c1VariantIdsRef.current = c1Variants.map(() => newVariantId())
  }
  const c1VariantIds = c1VariantIdsRef.current

  function addC1Variant(): void {
    if (c1Variants.length >= MAX_C1_VARIANTS) return
    c1VariantIdsRef.current = [...c1VariantIdsRef.current, newVariantId()]
    onChange({
      ...value,
      templates: {
        ...value.templates,
        c1_variants: [...c1Variants, { key: nextC1VariantKey(c1Variants), templateName: '' }],
      },
    })
  }

  function removeC1Variant(index: number): void {
    c1VariantIdsRef.current = c1VariantIdsRef.current.filter((_, i) => i !== index)
    onChange({
      ...value,
      templates: { ...value.templates, c1_variants: c1Variants.filter((_, i) => i !== index) },
    })
  }

  function patchC1Variant(index: number, partial: Partial<CampaignC1Variant>): void {
    onChange({
      ...value,
      templates: {
        ...value.templates,
        c1_variants: c1Variants.map((v, i) => (i === index ? { ...v, ...partial } : v)),
      },
    })
  }

  function setPromptId(id: string): void {
    onChange({ ...value, prompt_id: id })
  }

  function toggleOverride(enabled: boolean): void {
    onChange({
      ...value,
      business_hours_override: enabled ? DEFAULT_BUSINESS_HOURS : null,
    })
  }

  function setBusinessHours(patch: Partial<BusinessHours>): void {
    if (value.business_hours_override === null) return
    onChange({
      ...value,
      business_hours_override: { ...value.business_hours_override, ...patch },
    })
  }

  function toggleWeekday(day: number): void {
    if (value.business_hours_override === null) return
    const cur = new Set(value.business_hours_override.weekdays)
    if (cur.has(day)) cur.delete(day)
    else cur.add(day)
    setBusinessHours({ weekdays: [...cur].sort((a, b) => a - b) })
  }

  // Story 3.20 follow-up (user feedback 2026-07-15): a campaign that has
  // adopted `followup` no longer has any use for the legacy cadence
  // (C1-C4)/templates sections below. `hideLegacyCadence` covers `lead_new`
  // (which never read these fields at all, even before adopting followup);
  // `value.followup !== undefined` additionally covers any OTHER campaign
  // type that later migrates onto followup (Stories 3.21/3.22) without this
  // component needing another change.
  const showLegacyCadence = !hideLegacyCadence && value.followup === undefined

  return (
    <fieldset className="grid gap-6" disabled={disabled} aria-label="Configuração da campaign">
      {showLegacyCadence ? (
        <>
          {/* -------- Cadence ------------------------------------------------ */}
          <section className="grid gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Cadência (C1 → C2 → C3)
            </h2>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="grid gap-2">
                <Label htmlFor="cadence-c1">C1 — disparo inicial</Label>
                <Select
                  id="cadence-c1"
                  value={value.cadence.c1}
                  onChange={(e) => setCadence({ c1: e.target.value as CampaignCadence['c1'] })}
                >
                  <option value="immediate">Imediato</option>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="cadence-c2-time">C2 — horário (HH:MM)</Label>
                <Input
                  id="cadence-c2-time"
                  value={value.cadence.c2_time}
                  onChange={(e) => setCadence({ c2_time: e.target.value })}
                  placeholder="09:00"
                  pattern="^([01]\d|2[0-3]):[0-5]\d$"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="cadence-c3-offset">C3 — offset (ex: -1h)</Label>
                <Input
                  id="cadence-c3-offset"
                  value={value.cadence.c3_offset}
                  onChange={(e) => setCadence({ c3_offset: e.target.value })}
                  placeholder="-1h"
                  pattern="^-?\d+(h|m)$"
                  required
                />
              </div>
            </div>
          </section>

          {/* -------- C4 "Lembrete na hora" --------------------------------- */}
          <section className="grid gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              C4 — Lembrete na hora (opcional)
            </h2>
            <p className="text-sm text-muted-foreground">
              4º toque que dispara pouco antes da reunião e envia o link da reunião. Deixe o
              template vazio para manter desligado.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="tpl-c4">Template C4</Label>
                <Select
                  id="tpl-c4"
                  value={value.templates.c4 ?? ''}
                  onChange={(e) => setTemplate('c4', e.target.value)}
                >
                  <option value="">Desligado</option>
                  {approvedTemplates.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.label}
                    </option>
                  ))}
                  {c4OptionMissing && (
                    <option value={value.templates.c4} disabled>
                      {value.templates.c4} (indisponível)
                    </option>
                  )}
                </Select>
                {c4OptionMissing && (
                  <p className="text-xs text-destructive">
                    Template referenciado não está mais APPROVED.
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="cadence-c4-offset">C4 — offset (ex: -1m)</Label>
                <Input
                  id="cadence-c4-offset"
                  value={value.cadence.c4_offset ?? ''}
                  onChange={(e) => setCadence({ c4_offset: e.target.value })}
                  placeholder="-1m"
                  pattern="^(0|-?\d+(h|m))$"
                />
              </div>
            </div>
          </section>

          {/* -------- Templates --------------------------------------------- */}
          <section className="grid gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Templates (qualquer um APPROVED + ativo)
            </h2>
            {approvedTemplates.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Nenhum template aprovado disponível. Crie e aprove templates em{' '}
                <a href="/admin/templates" className="underline">
                  /admin/templates
                </a>{' '}
                antes de configurar.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-3">
              {(['c1', 'c2', 'c3'] as const).map((slot) => {
                const current = value.templates[slot]
                const optionMissing = current !== '' && !templateNames.includes(current)
                return (
                  <div key={slot} className="grid gap-2">
                    <Label htmlFor={`tpl-${slot}`}>Template {slot.toUpperCase()}</Label>
                    <Select
                      id={`tpl-${slot}`}
                      value={current}
                      onChange={(e) => setTemplate(slot, e.target.value)}
                      required
                    >
                      <option value="">Selecione…</option>
                      {approvedTemplates.map((t) => (
                        <option key={t.name} value={t.name}>
                          {t.label}
                        </option>
                      ))}
                      {optionMissing && (
                        <option value={current} disabled>
                          {current} (indisponível)
                        </option>
                      )}
                    </Select>
                    {optionMissing && (
                      <p className="text-xs text-destructive">
                        Template referenciado não está mais APPROVED.
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </section>

          {/* -------- Teste A/B/C do C1 (2026-07-30, user request) ---------- */}
          <section className="grid gap-3 rounded-lg border bg-muted/20 p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Teste A/B/C do C1 (confirmação inicial)
                </h2>
                <p className="text-sm text-muted-foreground">
                  Opcional. Cada lead cai numa variante de forma determinística (o mesmo lead
                  sempre recebe a mesma variante). Sem variantes configuradas, todo lead recebe o
                  Template C1 selecionado acima.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addC1Variant}
                disabled={disabled || c1Variants.length >= MAX_C1_VARIANTS}
              >
                Adicionar variante
              </Button>
            </div>

            {c1Variants.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma variante configurada.</p>
            ) : (
              <ul className="grid gap-3">
                {c1Variants.map((variant, index) => {
                  const optionMissing =
                    variant.templateName !== '' && !templateNames.includes(variant.templateName)
                  return (
                    <li
                      key={c1VariantIds[index]}
                      className="grid gap-3 rounded-md border bg-background p-3 sm:grid-cols-[1fr_2fr_auto]"
                    >
                      <div className="grid gap-2">
                        <Label htmlFor={`c1-variant-${index}-key`}>
                          Variante {index + 1} — chave
                        </Label>
                        <Input
                          id={`c1-variant-${index}-key`}
                          value={variant.key}
                          maxLength={32}
                          onChange={(e) => patchC1Variant(index, { key: e.target.value })}
                          disabled={disabled}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor={`c1-variant-${index}-template`}>Template</Label>
                        <Select
                          id={`c1-variant-${index}-template`}
                          value={variant.templateName}
                          onChange={(e) => patchC1Variant(index, { templateName: e.target.value })}
                          disabled={disabled || approvedTemplates.length === 0}
                        >
                          <option value="">— selecione —</option>
                          {approvedTemplates.map((t) => (
                            <option key={t.name} value={t.name}>
                              {t.label}
                            </option>
                          ))}
                          {optionMissing && (
                            <option value={variant.templateName} disabled>
                              {variant.templateName} (indisponível)
                            </option>
                          )}
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeC1Variant(index)}
                          disabled={disabled}
                        >
                          Remover
                        </Button>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          Esta campanha usa o motor de followup (abaixo) — a cadência C1-C4 e os templates legados
          não se aplicam e ficam ocultos.
        </p>
      )}

      {/* -------- Prompt ------------------------------------------------ */}
      <section className="grid gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Prompt (campanha → IA)
        </h2>
        <div className="grid gap-2">
          <Label htmlFor="prompt-id">prompt_id</Label>
          <Select
            id="prompt-id"
            value={value.prompt_id}
            onChange={(e) => setPromptId(e.target.value)}
          >
            <option value="">Sem prompt vinculado</option>
            {prompts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </Select>
          {value.prompt_id === '' && (
            <p className="text-xs text-muted-foreground">
              Sem prompt a campaign não pode ser ativada — vincule via{' '}
              <a href="/admin/prompts" className="underline">
                /admin/prompts
              </a>
              .
            </p>
          )}
        </div>
      </section>

      {/* -------- Business hours override ------------------------------- */}
      <section className="grid gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Janela operacional
        </h2>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={overrideEnabled}
            onChange={(e) => toggleOverride(e.target.checked)}
          />
          <span>Sobrescrever janela operacional global desta campaign</span>
        </label>

        {overrideEnabled && value.business_hours_override !== null && (
          <div className="grid gap-3 rounded-md border bg-muted/20 p-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="grid gap-2">
                <Label htmlFor="bh-tz">Timezone</Label>
                <Input
                  id="bh-tz"
                  value={value.business_hours_override.timezone}
                  onChange={(e) => setBusinessHours({ timezone: e.target.value })}
                  placeholder="America/Sao_Paulo"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="bh-start">Início (HH:MM)</Label>
                <Input
                  id="bh-start"
                  value={value.business_hours_override.start}
                  onChange={(e) => setBusinessHours({ start: e.target.value })}
                  pattern="^([01]\d|2[0-3]):[0-5]\d$"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="bh-end">Fim (HH:MM)</Label>
                <Input
                  id="bh-end"
                  value={value.business_hours_override.end}
                  onChange={(e) => setBusinessHours({ end: e.target.value })}
                  pattern="^([01]\d|2[0-3]):[0-5]\d$"
                  required
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Dias úteis">
              {WEEKDAY_LABELS.map((d) => {
                const checked = value.business_hours_override?.weekdays.includes(d.value) ?? false
                return (
                  <label
                    key={d.value}
                    className={
                      checked
                        ? 'rounded-md border border-primary bg-primary px-3 py-1 text-sm text-primary-foreground'
                        : 'rounded-md border px-3 py-1 text-sm'
                    }
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      onChange={() => toggleWeekday(d.value)}
                    />
                    {d.label}
                  </label>
                )
              })}
            </div>
          </div>
        )}
      </section>
    </fieldset>
  )
}

export default CampaignConfigForm
